import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Link, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddOutlined from '@mui/icons-material/AddOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import NorthEastOutlined from '@mui/icons-material/NorthEastOutlined';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import { AuthContext } from '../context/auth-context';
import { EmptyState, PageToolbar, StatusPill, useNotify } from '../ui';
import { bidActions, clearStoredBid } from '../../store/bid';
import { statusLabel, statusTone } from '../../utils/bidStatus';
import { useWorkspace } from '../../Hooks/useWorkspace';
import { EM_DASH } from '../../utils/formatDate';
import BidFormDialog from './BidFormDialog';
import { useBid } from './useBids';
import { useOpenLinkedProject } from './useOpenLinkedProject';
import { divisionLabel, formatBidDueText, formatLongDay } from './bidFormat';
import { useDelayedFlag } from './useDelayedFlag';

const SOURCE_LABELS = { manual: 'Entered by hand', bidtracer: 'BidTracer' };

/** "Oct 18, 2026 by Jeff Peterson", or just the date when no name is known. */
const dateBy = (value, name) =>
  name ? `${formatLongDay(value)} by ${name}` : formatLongDay(value);

// One label / value pair in the details card.
const Detail = ({ label, children, wide = false, numeric = false }) => {
  const { ft } = useTheme().palette;
  const isEmpty =
    children === null || children === undefined || children === '';
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: 0,
        gridColumn: wide ? '1 / -1' : undefined,
      }}
    >
      <Typography
        variant="fieldLabel"
        component="dt"
        sx={{ color: ft.neutrals.textBody }}
      >
        {label}
      </Typography>
      <Box
        component="dd"
        sx={{
          m: 0,
          fontSize: 14,
          color: isEmpty ? ft.neutrals.textDisabled : ft.neutrals.textPrimary,
          fontVariantNumeric: numeric ? 'tabular-nums' : undefined,
          overflowWrap: 'anywhere',
        }}
      >
        {isEmpty ? EM_DASH : children}
      </Box>
    </Box>
  );
};

const cardSx = (ft) => ({
  maxWidth: 960,
  border: `1px solid ${ft.neutrals.divider}`,
  borderRadius: `${ft.radius.card}px`,
  pt: 2.5,
  px: 3,
  pb: 3,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const detailsGridSx = {
  m: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  rowGap: '18px',
  columnGap: 4,
};

const CardSkeleton = () => {
  const { ft } = useTheme().palette;
  const bar = {
    bgcolor: ft.neutrals.borderLight,
    borderRadius: `${ft.radius.control}px`,
    transform: 'none',
  };
  return (
    <Box data-testid="bid-overview-skeleton" sx={cardSx(ft)}>
      <Skeleton
        variant="rectangular"
        animation="wave"
        height={10}
        width={88}
        sx={bar}
      />
      <Box sx={detailsGridSx}>
        {Array.from({ length: 8 }, (_, i) => (
          <Box
            key={i}
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
          >
            <Skeleton
              variant="rectangular"
              animation="wave"
              height={10}
              width={80}
              sx={bar}
            />
            <Skeleton
              variant="rectangular"
              animation="wave"
              height={14}
              width="60%"
              sx={bar}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * BidOverviewPage — /bids/overview (ticket #295, plan §4.11). The selected
 * bid's details, Edit bid, and the jump to its linked project; "No bid
 * selected" when there is none. Reads the bid through useWorkspace().
 */
const BidOverviewPage = () => {
  const { ft } = useTheme().palette;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notify = useNotify();
  const { userId } = useContext(AuthContext);
  const workspace = useWorkspace();
  const openProject = useOpenLinkedProject();
  const { bid, isLoading, error, notFound, reload, setBid } = useBid(
    workspace.id
  );
  const showSkeleton = useDelayedFlag(isLoading);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // The bid was deleted elsewhere (or access was lost): drop the selection.
  useEffect(() => {
    if (!notFound) return;
    dispatch(bidActions.clearBid());
    clearStoredBid(userId);
    notify.info('That bid is no longer available.');
    // notify is stable for the provider's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notFound, dispatch, userId]);

  const pageSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    px: 3,
    py: 2,
  };

  const createDialog = (
    <BidFormDialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
  );

  if (!workspace.id) {
    return (
      <Box sx={{ ...pageSx, alignItems: 'center', justifyContent: 'center' }}>
        <EmptyState
          icon={<RequestQuoteOutlined />}
          title="No bid selected"
          description="Pick a bid to see its details, or create one to get started."
          action={
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button variant="outlined" onClick={() => navigate('/bids')}>
                View all bids
              </Button>
              <Button
                variant="contained"
                startIcon={<AddOutlined />}
                onClick={() => setIsCreateOpen(true)}
              >
                New bid
              </Button>
            </Box>
          }
          sx={{ width: '100%', maxWidth: 520, py: 6 }}
        />
        {createDialog}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={pageSx}>
        <EmptyState
          title="This bid could not be loaded."
          description="Check your connection and try again."
          action={
            <Button variant="outlined" onClick={reload}>
              Try again
            </Button>
          }
          sx={{ maxWidth: 960 }}
        />
      </Box>
    );
  }

  if (isLoading || !bid) {
    return <Box sx={pageSx}>{showSkeleton && <CardSkeleton />}</Box>;
  }

  return (
    <Box sx={pageSx}>
      <PageToolbar
        sx={{ px: 0, py: 0, alignItems: 'center', gap: 1.5 }}
        filters={
          <>
            <StatusPill
              tone={statusTone(bid.status)}
              label={statusLabel(bid.status)}
            />
            <Typography
              variant="caption"
              sx={{ color: ft.neutrals.textSecondary }}
            >
              Updated {dateBy(bid.updatedAt, bid.updatedByName)}
            </Typography>
          </>
        }
        actions={
          <Button
            variant="contained"
            startIcon={<EditOutlined />}
            onClick={() => setIsEditOpen(true)}
          >
            Edit bid
          </Button>
        }
      />

      <Box
        component="section"
        aria-labelledby="bid-details-label"
        sx={cardSx(ft)}
      >
        <Typography
          id="bid-details-label"
          variant="sectionLabel"
          component="h2"
          sx={{ m: 0, color: ft.neutrals.textSecondary }}
        >
          Bid details
        </Typography>
        <Box component="dl" sx={detailsGridSx}>
          <Detail label="Bid name">{bid.name}</Detail>
          <Detail label="Bid number" numeric>
            {bid.bidNumber}
          </Detail>
          <Detail label="Division">
            {divisionLabel(bid.division)}
            {bid.division?.jobNumber && (
              <Box component="span" sx={{ color: ft.neutrals.textSecondary }}>
                {' '}
                · {bid.division.jobNumber}
              </Box>
            )}
          </Detail>
          <Detail label="Bid due" numeric>
            {bid.bidDueAt ? formatBidDueText(bid.bidDueAt) : null}
          </Detail>
          <Detail label="Linked project" wide>
            {bid.project && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Link
                  component="button"
                  underline="hover"
                  onClick={() => openProject(bid.project)}
                  sx={{
                    alignSelf: 'flex-start',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    fontSize: 14,
                    fontWeight: ft.fontWeights.medium,
                    textAlign: 'left',
                  }}
                >
                  {bid.project.number} — {bid.project.name}
                  <NorthEastOutlined sx={{ fontSize: 16 }} />
                </Link>
                <Typography
                  variant="caption"
                  sx={{ color: ft.neutrals.textSecondary }}
                >
                  Opens this project in Projects.
                </Typography>
              </Box>
            )}
          </Detail>
          <Detail label="BidTracer ID" numeric>
            {bid.bidtracerId}
          </Detail>
          <Detail label="Source">
            {SOURCE_LABELS[bid.source] ?? bid.source}
          </Detail>
          <Detail label="Created">
            {dateBy(bid.createdAt, bid.createdByName)}
          </Detail>
        </Box>
      </Box>

      <BidFormDialog
        open={isEditOpen}
        mode="edit"
        bid={bid}
        onClose={() => setIsEditOpen(false)}
        onSaved={setBid}
      />
    </Box>
  );
};

export default BidOverviewPage;
